import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Clear from '@mui/icons-material/Clear';
import { ReactNode, useState } from 'react';
import {
  Controller,
  UseFieldArrayReturn,
  UseFormReturn,
} from 'react-hook-form';
import SharedAutocomplete from '../../../../../../shared/shared-autocomplete';
import UserAvatar from '../../../../../../shared/user-avatar';
import { GPanelPermissionsModel } from '../../../../../../../types';
import {
  buildNewSharedDriveMember,
  NewMemberDefaults,
  SHARED_DRIVE_ROLE_OPTIONS,
  SharedDriveMembersFormValues,
} from './shared-drive-members';

interface SharedDriveMembersEditorProps {
  form: UseFormReturn<SharedDriveMembersFormValues>;
  fieldArray: UseFieldArrayReturn<
    SharedDriveMembersFormValues,
    'aclResources',
    'id'
  >;
  /** Members as loaded from the server, used to reset changeType. Empty when creating a drive. */
  originalMembers: GPanelPermissionsModel[];
  newMemberDefaults: NewMemberDefaults;
  isLoading?: boolean;
  /** Rendered between the search field and the member list. */
  listHeader?: ReactNode;
}

export default function SharedDriveMembersEditor({
  form,
  fieldArray,
  originalMembers,
  newMemberDefaults,
  isLoading = false,
  listHeader,
}: SharedDriveMembersEditorProps) {
  const [inputValue, setInputValue] = useState('');
  const { control, setValue, watch } = form;
  const { fields, append, remove } = fieldArray;

  return (
    <Box>
      <Stack flexDirection="row" gap={1} sx={{ mb: 1 }}>
        <Box sx={{ width: '100%' }}>
          <SharedAutocomplete
            sources={['users', 'groups']}
            autocompleteValue={inputValue}
            setAutoCompleteValue={setInputValue}
            freeSolo
            onSelection={(value, selectedType) => {
              if (!value) return;
              if (fields.find((acl) => acl.userEmail === value)) return;

              append(
                buildNewSharedDriveMember(
                  value,
                  selectedType,
                  newMemberDefaults
                )
              );

              setInputValue('');
            }}
          />
        </Box>
      </Stack>
      {listHeader}

      {isLoading ? (
        <Stack gap={1}>
          <Skeleton variant="rounded" height={60} />
        </Stack>
      ) : (
        <List sx={{ maxHeight: 300, overflowY: 'auto' }}>
          {fields.map((acl, index) => {
            const watchedAcl = watch(`aclResources.${index}`);

            return (
              <Stack key={acl.id}>
                <ListItem
                  dense
                  secondaryAction={
                    <Stack flexDirection="row" gap={1} alignItems="center">
                      <Controller
                        name={`aclResources.${index}.role`}
                        control={control}
                        rules={{
                          required: 'Role is required.',
                        }}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);

                                // handle changeType; match by email, since removing a row shifts indexes
                                if (watchedAcl.changeType !== 'add') {
                                  const originalRole = originalMembers.find(
                                    (member) =>
                                      member.userEmail === watchedAcl.userEmail
                                  )?.role;
                                  if (e.target.value === originalRole) {
                                    setValue(
                                      `aclResources.${index}.changeType`,
                                      null,
                                      { shouldDirty: true }
                                    );
                                  } else {
                                    setValue(
                                      `aclResources.${index}.changeType`,
                                      'change',
                                      { shouldDirty: true }
                                    );
                                  }
                                }
                              }}
                              inputProps={{
                                id: `role-${index}`,
                              }}
                              InputLabelProps={{
                                htmlFor: `role-${index}`,
                              }}
                              select
                              label="Role"
                              size="small"
                              sx={{ width: 175 }}
                            >
                              {SHARED_DRIVE_ROLE_OPTIONS.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                            <Tooltip arrow title="Remove">
                              <IconButton
                                size="small"
                                aria-label={`Remove ${acl.userEmail}`}
                                onClick={() => remove(index)}
                              >
                                <Clear />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      />
                    </Stack>
                  }
                >
                  <ListItemAvatar>
                    <UserAvatar userEmail={acl.userEmail || ''} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={acl.userEmail}
                    primaryTypographyProps={{
                      maxWidth: 200,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    }}
                  />
                </ListItem>
              </Stack>
            );
          })}
        </List>
      )}
    </Box>
  );
}
